import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { MetaApiResponse } from '@/utils/types/CommonApiResponse';
import {
  getAgencyRevenueAnalytics,
  getAgencyRevenueBreakdown,
  getAgencyMontlyRevenue,
  fetchAgencyBusinessInvoice
} from './api';
import { MonthlyRevenue, RevenueBreakdown } from './types';

export function useGetAgencyRevenueAnalytics(params?: Record<string, any>, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['agency_revenue_analytics', params],
    queryFn: () => getAgencyRevenueAnalytics(params),
    ...options
  });
}

export function useGetAgencyRevenueBreakdown(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: RevenueBreakdown[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: any[]; meta: MetaApiResponse }>({
    queryKey: ['agency_revenue_breakdown', params],
    queryFn: () => getAgencyRevenueBreakdown(params),
    ...options
  });
}

export function useGetAgencyMontlyRevenue(
  id: number,
  params?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      { id: number; data: MonthlyRevenue[]; meta: MetaApiResponse },
      Error,
      { id: number; data: RevenueBreakdown[]; meta: MetaApiResponse },
      readonly unknown[]
    >,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ id: number; data: any[]; meta: MetaApiResponse }>({
    queryKey: ['agency_monthly_revenue', id, params],
    queryFn: () => getAgencyMontlyRevenue(id, params),
    enabled: !!id,
    ...options
  });
}

export function useFetchAgencyBusinessInvoice(businessId?: number) {
  return useMutation({
    mutationFn: (id: number) => fetchAgencyBusinessInvoice(businessId!, id),
    onSuccess: (data: { url: string }) => {
      if (data.url) window.open(data.url, '_blank');
    }
  });
}
